import { LL } from 'i18n/i18n.service';
import { TranslatedString } from 'models/translated-string';

/**
 * The user-facing messages the image-upload path can show when a pick or an
 * upload fails.
 *
 * These live in their own module, separate from `ImageService`, for one
 * reason: `ImageService` is an Angular service that needs dependency injection
 * and a browser DOM, so its message text cannot be checked by a plain unit
 * test. These builders are pure functions, so `image-upload-messages.test.ts`
 * can assert that every message the user could see really comes from the
 * translation catalog instead of being a hardcoded English literal — which is
 * what all four of them used to be (#4366).
 */

/** The selected file could not be read off disk at all. */
export function imageReadFailedMessage(): TranslatedString {
  return LL.errors.userFacing.imageReadFailed();
}

/** The file was read, but the image is smaller than the minimum we accept. */
export function imageTooSmallMessage(minSizePx: number): TranslatedString {
  return LL.errors.userFacing.imageTooSmall({ minSize: minSizePx });
}

/** The file was read but the browser could not decode it as an image. */
export function imageDecodeFailedMessage(): TranslatedString {
  return LL.errors.userFacing.imageDecodeFailed();
}

/**
 * The image could not be turned into the uploaded sizes, or sending it to
 * storage failed.
 *
 * This replaces the raw exception text the upload path used to show the user,
 * which was both untranslatable and meaningless to them. Nothing is lost for
 * debugging: `ImageService.createCanvasAndUploadAsync` reports each of its
 * failure paths to Sentry through its `reportUploadFailureOnce`.
 */
export function imageUploadFailedMessage(): TranslatedString {
  return LL.errors.userFacing.imageUploadFailed();
}
